import React, { useState, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSegment,
  IonSegmentButton,
  IonIcon,
  IonLoading,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonChip,
  IonText,
  IonList,
  IonListHeader,
  IonGrid,
  IonRow,
  IonCol,
  IonToggle,
  IonDatetime,
  IonModal,
  IonAlert,
  IonProgressBar,
} from '@ionic/react';
import {
  refreshOutline,
  trendingUpOutline,
  trendingDownOutline,
  cashOutline,
  barChartOutline,
  pieChartOutline,
  timeOutline,
  calendarOutline,
  alertCircleOutline,
  checkmarkCircleOutline,
  cartOutline,
  cubeOutline,
  walletOutline,
  analyticsOutline,
  closeOutline,
  downloadOutline,
} from 'ionicons/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  analyticsApi,
  formatCurrency,
  formatPercent,
  getProfitMarginColor,
  formatDate,
  formatDateTime,
} from '../api/analytics';
import { AnalyticsData, OrdersSummaryMetrics } from 'shared';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

type DateRange = 'today' | 'week' | 'month' | 'year' | 'custom';

const Analytics: React.FC = () => {
  const { t } = useLanguage();
  const { colors } = useTheme();
  const queryClient = useQueryClient();

  // State
  const [selectedTab, setSelectedTab] = useState<'overview' | 'products' | 'inventory' | 'trends'>('overview');
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [showExportAlert, setShowExportAlert] = useState(false);

  // Calculate date range for API calls
  const dateRangeParams = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateRange) {
      case 'today':
        return { startDate: today, endDate: now };
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        return { startDate: weekStart, endDate: now };
      }
      case 'month': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: monthStart, endDate: now };
      }
      case 'year': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return { startDate: yearStart, endDate: now };
      }
      case 'custom':
        return {
          startDate: customStartDate ? new Date(customStartDate) : today,
          endDate: customEndDate ? new Date(customEndDate) : now,
        };
      default:
        return { startDate: today, endDate: now };
    }
  }, [dateRange, customStartDate, customEndDate]);

  // Fetch basic analytics
  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ['analytics', dateRangeParams],
    queryFn: () => analyticsApi.getAnalytics(dateRangeParams.startDate, dateRangeParams.endDate),
    staleTime: 30000,
  });

  // Fetch orders summary
  const {
    data: ordersSummary,
    isLoading: ordersLoading,
  } = useQuery({
    queryKey: ['orders-summary', dateRange],
    queryFn: () => analyticsApi.getOrdersSummary({
      tab: dateRange,
      date: dateRangeParams.startDate.toISOString().split('T')[0],
    }),
    staleTime: 30000,
  });

  // Fetch product profitability
  const {
    data: productProfitability,
    isLoading: profitabilityLoading,
  } = useQuery({
    queryKey: ['product-profitability', dateRangeParams],
    queryFn: () => analyticsApi.getProductProfitability(dateRangeParams.startDate, dateRangeParams.endDate),
    staleTime: 60000,
    enabled: selectedTab === 'products',
  });

  // Fetch best sellers
  const {
    data: bestSellers,
    isLoading: bestSellersLoading,
  } = useQuery({
    queryKey: ['best-sellers', dateRangeParams],
    queryFn: () => analyticsApi.getBestSellers(dateRangeParams.startDate, dateRangeParams.endDate),
    staleTime: 60000,
    enabled: selectedTab === 'products',
  });

  // Fetch slow movers
  const {
    data: slowMovers,
    isLoading: slowMoversLoading,
  } = useQuery({
    queryKey: ['slow-movers', dateRangeParams],
    queryFn: () => analyticsApi.getSlowMovingProducts(dateRangeParams.startDate, dateRangeParams.endDate),
    staleTime: 60000,
    enabled: selectedTab === 'inventory',
  });

  // Fetch inventory report
  const {
    data: inventoryReport,
    isLoading: inventoryLoading,
  } = useQuery({
    queryKey: ['inventory-report'],
    queryFn: () => analyticsApi.getInventoryReport(),
    staleTime: 120000,
    enabled: selectedTab === 'inventory',
  });

  // Fetch sales trend
  const {
    data: salesTrend,
    isLoading: trendLoading,
  } = useQuery({
    queryKey: ['sales-trend', dateRangeParams],
    queryFn: () => analyticsApi.getSalesTrend(dateRangeParams.startDate, dateRangeParams.endDate),
    staleTime: 60000,
    enabled: selectedTab === 'trends',
  });

  // Calculate metrics
  const totalRevenue = ordersSummary?.totalRevenue || analytics?.totalSales || 0;
  const totalProfit = ordersSummary?.totalProfit || analytics?.profit || 0;
  const totalOrders = ordersSummary?.totalOrders || analytics?.ordersCount || 0;
  const profitMargin = totalRevenue > 0 ? totalProfit / totalRevenue : 0;

  // Handle refresh
  const handleRefresh = async (event: CustomEvent) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['analytics'] }),
      queryClient.invalidateQueries({ queryKey: ['orders-summary'] }),
      queryClient.invalidateQueries({ queryKey: ['product-profitability'] }),
      queryClient.invalidateQueries({ queryKey: ['best-sellers'] }),
      queryClient.invalidateQueries({ queryKey: ['slow-movers'] }),
      queryClient.invalidateQueries({ queryKey: ['inventory-report'] }),
      queryClient.invalidateQueries({ queryKey: ['sales-trend'] }),
    ]);
    event.detail.complete();
  };

  // Handle date range change
  const handleDateRangeChange = (range: DateRange) => {
    if (range === 'custom') {
      setShowDateRangeModal(true);
    } else {
      setDateRange(range);
    }
  };

  const applyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      setShowDateRangeModal(false);
    }
  };

  // Export data
  const exportData = () => {
    // This would typically call an export API endpoint
    // For now, just show a success message
    setShowExportAlert(false);
  };

  // Loading state
  const isLoading = analyticsLoading || ordersLoading ||
    (selectedTab === 'products' && (profitabilityLoading || bestSellersLoading)) ||
    (selectedTab === 'inventory' && (inventoryLoading || slowMoversLoading)) ||
    (selectedTab === 'trends' && trendLoading);

  if (analyticsError) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/analytics" />
            </IonButtons>
            <IonTitle>{t('analytics') || 'Analytics'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Error loading analytics data</p>
            <IonButton fill="clear" onClick={() => refetchAnalytics()}>
              Retry
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/dashboard" />
          </IonButtons>
          <IonTitle>{t('analytics') || 'Analytics'}</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={() => setShowExportAlert(true)}>
              <IonIcon icon={downloadOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingIcon={refreshOutline}
            pullingText={t('pullToRefresh') || 'Pull to refresh'}
          />
        </IonRefresher>

        {/* Date Range Selector */}
        <div style={{ padding: '16px' }}>
          <IonCard>
            <IonCardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <IonText color="medium">
                  <h4 style={{ margin: 0 }}>{t('dateRange') || 'Date Range'}</h4>
                </IonText>
                <IonButton fill="clear" size="small" onClick={() => setShowDateRangeModal(true)}>
                  <IonIcon icon={calendarOutline} />
                </IonButton>
              </div>
              <IonSegment
                value={dateRange}
                onIonChange={(e) => handleDateRangeChange(e.detail.value as DateRange)}
              >
                <IonSegmentButton value="today">
                  <IonLabel>{t('today') || 'Today'}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="week">
                  <IonLabel>{t('week') || 'Week'}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="month">
                  <IonLabel>{t('month') || 'Month'}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="year">
                  <IonLabel>{t('year') || 'Year'}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Key Metrics Overview */}
        <div style={{ padding: '0 16px 16px' }}>
          <IonGrid>
            <IonRow>
              <IonCol size="6">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '14px', fontWeight: '600' }}>
                      {t('revenue') || 'Revenue'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: colors.text }}>
                      {formatCurrency(totalRevenue)}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                      <IonIcon icon={cashOutline} style={{ color: colors.success, fontSize: '16px' }} />
                      <IonText style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {t('totalRevenue') || 'Total Revenue'}
                      </IonText>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              <IonCol size="6">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '14px', fontWeight: '600' }}>
                      {t('profit') || 'Profit'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: colors.text }}>
                      {formatCurrency(totalProfit)}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                      <IonIcon icon={walletOutline} style={{ color: getProfitMarginColor(profitMargin), fontSize: '16px' }} />
                      <IonText style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {formatPercent(profitMargin)} {t('margin') || 'margin'}
                      </IonText>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '14px', fontWeight: '600' }}>
                      {t('orders') || 'Orders'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: colors.text }}>
                      {totalOrders.toLocaleString()}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                      <IonIcon icon={cartOutline} style={{ color: colors.primary, fontSize: '16px' }} />
                      <IonText style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {t('totalOrders') || 'Total Orders'}
                      </IonText>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              <IonCol size="6">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '14px', fontWeight: '600' }}>
                      {t('avgOrderValue') || 'Avg Order Value'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: colors.text }}>
                      {formatCurrency(totalOrders > 0 ? totalRevenue / totalOrders : 0)}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                      <IonIcon icon={analyticsOutline} style={{ color: colors.info, fontSize: '16px' }} />
                      <IonText style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {t('perOrder') || 'per order'}
                      </IonText>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>

        {/* Tab Navigation */}
        <div style={{ padding: '0 16px' }}>
          <IonSegment
            value={selectedTab}
            onIonChange={(e) => setSelectedTab(e.detail.value as any)}
          >
            <IonSegmentButton value="overview">
              <IonLabel>{t('overview') || 'Overview'}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="products">
              <IonLabel>{t('products') || 'Products'}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="inventory">
              <IonLabel>{t('inventory') || 'Inventory'}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="trends">
              <IonLabel>{t('trends') || 'Trends'}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '16px' }}>
          {selectedTab === 'overview' && (
            <div>
              {/* Top Selling Items */}
              {analytics?.topSellingItems && analytics.topSellingItems.length > 0 && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '16px' }}>
                      {t('topSellingItems') || 'Top Selling Items'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {analytics.topSellingItems.slice(0, 5).map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: index < analytics.topSellingItems.length - 1 ? `1px solid ${colors.borderLight}` : 'none'
                      }}>
                        <div style={{ flex: 1 }}>
                          <IonText style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: colors.text
                          }}>
                            {item.menuItem.product?.name || 'Unknown Product'}
                          </IonText>
                          <IonText style={{
                            fontSize: '12px',
                            color: colors.textSecondary
                          }}>
                            {item.quantitySold} sold
                          </IonText>
                        </div>
                        <IonText style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: colors.success
                        }}>
                          {formatCurrency(item.revenue)}
                        </IonText>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>
              )}

              {/* Performance Summary */}
              <IonCard style={{ marginTop: '16px' }}>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: '16px' }}>
                    {t('performanceSummary') || 'Performance Summary'}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <IonText style={{ color: colors.textSecondary, fontSize: '14px' }}>
                        {t('profitMargin') || 'Profit Margin'}
                      </IonText>
                      <IonText style={{ fontWeight: '600', fontSize: '14px' }}>
                        {formatPercent(profitMargin)}
                      </IonText>
                    </div>
                    <IonProgressBar
                      value={profitMargin}
                      color={profitMargin >= 0.5 ? 'success' : profitMargin >= 0.3 ? 'warning' : 'danger'}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ textAlign: 'center' }}>
                      <IonIcon icon={checkmarkCircleOutline} style={{ color: colors.success, fontSize: '24px' }} />
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSecondary }}>
                        {t('profitable') || 'Profitable'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <IonIcon icon={cubeOutline} style={{ color: colors.primary, fontSize: '24px' }} />
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSecondary }}>
                        {analytics?.topSellingItems?.length || 0} {t('products') || 'products'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <IonIcon icon={trendingUpOutline} style={{ color: colors.info, fontSize: '24px' }} />
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSecondary }}>
                        {t('growing') || 'Growing'}
                      </p>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>
            </div>
          )}

          {selectedTab === 'products' && (
            <div>
              {/* Product Profitability */}
              {productProfitability && productProfitability.length > 0 && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '16px' }}>
                      {t('productProfitability') || 'Product Profitability'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {productProfitability.slice(0, 10).map((product, index) => (
                      <div key={index} style={{
                        padding: '12px 0',
                        borderBottom: index < productProfitability.length - 1 ? `1px solid ${colors.borderLight}` : 'none'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <IonText style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: colors.text,
                            flex: 1
                          }}>
                            {product.menuItem.product?.name || 'Unknown Product'}
                          </IonText>
                          <IonText style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: getProfitMarginColor(product.profitMargin)
                          }}>
                            {formatPercent(product.profitMargin)}
                          </IonText>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <IonText style={{ color: colors.textSecondary }}>
                            {product.quantitySold} {t('sold') || 'sold'}
                          </IonText>
                          <div style={{ textAlign: 'right' }}>
                            <IonText style={{ color: colors.textSecondary }}>
                              {t('revenue') || 'Revenue'}: {formatCurrency(product.revenue)}
                            </IonText>
                            <IonText style={{ color: colors.textSecondary, display: 'block' }}>
                              {t('profit') || 'Profit'}: {formatCurrency(product.profit)}
                            </IonText>
                          </div>
                        </div>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>
              )}

              {/* Best Sellers */}
              {bestSellers && bestSellers.length > 0 && (
                <IonCard style={{ marginTop: '16px' }}>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '16px' }}>
                      {t('bestSellers') || 'Best Sellers'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {bestSellers.slice(0, 10).map((seller, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: index < bestSellers.length - 1 ? `1px solid ${colors.borderLight}` : 'none'
                      }}>
                        <div style={{ flex: 1 }}>
                          <IonText style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: colors.text
                          }}>
                            #{index + 1} {seller.menuItem.product?.name || 'Unknown Product'}
                          </IonText>
                          <IonText style={{
                            fontSize: '12px',
                            color: colors.textSecondary
                          }}>
                            {seller.quantitySold} {t('sold') || 'sold'} • {formatCurrency(seller.averageOrderValue)} {t('avg') || 'avg'}
                          </IonText>
                        </div>
                        <IonText style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: colors.success
                        }}>
                          {formatCurrency(seller.revenue)}
                        </IonText>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>
              )}
            </div>
          )}

          {selectedTab === 'inventory' && (
            <div>
              {/* Inventory Report */}
              {inventoryReport && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '16px' }}>
                      {t('inventorySummary') || 'Inventory Summary'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonGrid>
                      <IonRow>
                        <IonCol size="6">
                          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: colors.text, fontSize: '20px' }}>
                              {formatCurrency(inventoryReport.totalValue)}
                            </h3>
                            <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                              {t('totalValue') || 'Total Value'}
                            </p>
                          </div>
                        </IonCol>
                        <IonCol size="6">
                          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: colors.text, fontSize: '20px' }}>
                              {inventoryReport.totalProducts}
                            </h3>
                            <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                              {t('totalProducts') || 'Total Products'}
                            </p>
                          </div>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol size="12">
                          <div style={{ textAlign: 'center' }}>
                            {inventoryReport.lowStockItems > 0 ? (
                              <div>
                                <IonIcon
                                  icon={alertCircleOutline}
                                  style={{ color: colors.warning, fontSize: '32px' }}
                                />
                                <h4 style={{ margin: '8px 0 0 0', color: colors.text }}>
                                  {inventoryReport.lowStockItems} {t('lowStockItems') || 'Low Stock Items'}
                                </h4>
                              </div>
                            ) : (
                              <div>
                                <IonIcon
                                  icon={checkmarkCircleOutline}
                                  style={{ color: colors.success, fontSize: '32px' }}
                                />
                                <h4 style={{ margin: '8px 0 0 0', color: colors.text }}>
                                  {t('allStockLevelsGood') || 'All Stock Levels Good'}
                                </h4>
                              </div>
                            )}
                          </div>
                        </IonCol>
                      </IonRow>
                    </IonGrid>

                    {/* Category Breakdown */}
                    {inventoryReport.categories && inventoryReport.categories.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
                          {t('categoryBreakdown') || 'Category Breakdown'}
                        </h4>
                        {inventoryReport.categories.map((category, index) => (
                          <div key={index} style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <IonText style={{ fontSize: '14px', color: colors.text }}>
                                {category.category}
                              </IonText>
                              <IonText style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
                                {formatCurrency(category.value)}
                              </IonText>
                            </div>
                            <IonProgressBar value={category.value / inventoryReport.totalValue} />
                            <IonText style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
                              {category.itemCount} {t('items') || 'items'}
                            </IonText>
                          </div>
                        ))}
                      </div>
                    )}
                  </IonCardContent>
                </IonCard>
              )}

              {/* Slow Moving Products */}
              {slowMovers && slowMovers.length > 0 && (
                <IonCard style={{ marginTop: '16px' }}>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '16px' }}>
                      {t('slowMovingProducts') || 'Slow Moving Products'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {slowMovers.slice(0, 10).map((product, index) => (
                      <div key={index} style={{
                        padding: '12px 0',
                        borderBottom: index < slowMovers.length - 1 ? `1px solid ${colors.borderLight}` : 'none'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <IonText style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: colors.text,
                            flex: 1
                          }}>
                            {product.menuItem.product?.name || 'Unknown Product'}
                          </IonText>
                          <IonText style={{
                            fontSize: '14px',
                            color: colors.warning,
                            fontWeight: '600'
                          }}>
                            {product.quantitySold} {t('sold') || 'sold'}
                          </IonText>
                        </div>
                        <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                          <div>{t('lastSold') || 'Last sold'}: {product.lastSold ? formatDate(product.lastSold) : (t('never') || 'Never')}</div>
                          <div>{t('inventoryValue') || 'Inventory value'}: {formatCurrency(product.inventoryValue)}</div>
                        </div>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>
              )}
            </div>
          )}

          {selectedTab === 'trends' && (
            <div>
              {/* Sales Trend */}
              {salesTrend && salesTrend.length > 0 && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle style={{ fontSize: '16px' }}>
                      {t('salesTrend') || 'Sales Trend'}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {/* Simple trend visualization */}
                    <div style={{ marginBottom: '16px' }}>
                      {salesTrend.slice(0, 7).map((day, index) => (
                        <div key={index} style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <IonText style={{ fontSize: '12px', color: colors.textSecondary }}>
                              {formatDate(day.date)}
                            </IonText>
                            <IonText style={{ fontSize: '12px', fontWeight: '600' }}>
                              {formatCurrency(day.revenue)}
                            </IonText>
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <div style={{
                              flex: day.revenue / Math.max(...salesTrend.map(d => d.revenue)),
                              height: '20px',
                              backgroundColor: colors.primary,
                              borderRadius: '2px'
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Trend Summary */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      textAlign: 'center',
                      padding: '12px',
                      backgroundColor: colors.backgroundSecondary,
                      borderRadius: '8px'
                    }}>
                      <div>
                        <IonIcon icon={trendingUpOutline} style={{ color: colors.success, fontSize: '24px' }} />
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSecondary }}>
                          {t('totalRevenue') || 'Total Revenue'}
                        </p>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: colors.text }}>
                          {formatCurrency(salesTrend.reduce((sum, day) => sum + day.revenue, 0))}
                        </p>
                      </div>
                      <div>
                        <IonIcon icon={walletOutline} style={{ color: colors.info, fontSize: '24px' }} />
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSecondary }}>
                          {t('totalProfit') || 'Total Profit'}
                        </p>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: colors.text }}>
                          {formatCurrency(salesTrend.reduce((sum, day) => sum + day.profit, 0))}
                        </p>
                      </div>
                      <div>
                        <IonIcon icon={cartOutline} style={{ color: colors.primary, fontSize: '24px' }} />
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.textSecondary }}>
                          {t('totalOrders') || 'Total Orders'}
                        </p>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: colors.text }}>
                          {salesTrend.reduce((sum, day) => sum + day.orders, 0)}
                        </p>
                      </div>
                    </div>
                  </IonCardContent>
                </IonCard>
              )}
            </div>
          )}
        </div>

        <IonLoading isOpen={isLoading} message={t('loading') || 'Loading...'} />

        {/* Date Range Modal */}
        <IonModal isOpen={showDateRangeModal} onDidDismiss={() => setShowDateRangeModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('customDateRange') || 'Custom Date Range'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowDateRangeModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ padding: '20px' }}>
            <IonItem>
              <IonLabel position="stacked">{t('startDate') || 'Start Date'}</IonLabel>
              <IonDatetime
                value={customStartDate}
                onIonChange={(e) => setCustomStartDate(Array.isArray(e.detail.value) ? e.detail.value[0] : e.detail.value || '')}
                presentation="date"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('endDate') || 'End Date'}</IonLabel>
              <IonDatetime
                value={customEndDate}
                onIonChange={(e) => setCustomEndDate(Array.isArray(e.detail.value) ? e.detail.value[0] : e.detail.value || '')}
                presentation="date"
              />
            </IonItem>

            <div style={{ marginTop: '24px' }}>
              <IonButton
                expand="block"
                onClick={applyCustomDateRange}
                disabled={!customStartDate || !customEndDate}
              >
                {t('apply') || 'Apply'}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Export Alert */}
        <IonAlert
          isOpen={showExportAlert}
          onDidDismiss={() => setShowExportAlert(false)}
          header={t('exportAnalytics') || 'Export Analytics'}
          message={t('exportAnalyticsMessage') || 'Export current analytics data to CSV file for detailed analysis.'}
          buttons={[
            {
              text: t('cancel') || 'Cancel',
              role: 'cancel',
            },
            {
              text: t('export') || 'Export',
              handler: exportData,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Analytics;